import {
	Text,
	HStack,
	Button,
	Checkbox,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import {
	ACCOUNT_API_CONNECT_TO_HUBSPOT,
	MARK_NOT_PRIMARY_ACCOUNT,
	MARK_PRIMARY_ACCOUNT,
} from 'apiRoutes';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import { StandardModal } from 'components/common/Modal/StandardModal';
import { MontelAccount } from 'domain/account/interfaces';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useEffect, useState } from 'react';

interface EditHubSpotModalProps {
	showModal: boolean;
	setShowModal: (showModal: boolean) => void;
	account: MontelAccount;
	onSuccess: () => void;
}

export const ManageHubSpotConnectionModal: FunctionComponent<
	EditHubSpotModalProps
> = ({ showModal, setShowModal, account, onSuccess }) => {
	const [isPrimary, setIsPrimary] = useState<boolean>(account.isPrimary);

	useEffect(() => {
		if (showModal) {
			setIsPrimary(account.isPrimary);
		}
	}, [showModal, account.isPrimary]);

	const [markPrimary, markPrimaryStatus] = useSubmit(
		MARK_PRIMARY_ACCOUNT,
		POST,
		{
			messageOnSuccess: 'Account marked as primary for HubSpot',
			messageOnError: 'Error marking account as primary',
			showApiError: true,
		},
	);

	const [markNonPrimary, markNonPrimaryStatus] = useSubmit(
		MARK_NOT_PRIMARY_ACCOUNT,
		POST,
		{
			messageOnSuccess: 'Account marked as non-primary for HubSpot',
			messageOnError: 'Error marking account as non-primary',
			showApiError: true,
		},
	);

	const [syncToHubSpot, syncStatus] = useSubmit(
		ACCOUNT_API_CONNECT_TO_HUBSPOT,
		POST,
		{
			messageOnSuccess: 'Account synced to HubSpot',
			messageOnError: 'Error syncing account to HubSpot',
			showApiError: true,
		},
	);

	const onUpdatePrimary = async () => {
		const submitPrimary = isPrimary ? markPrimary : markNonPrimary;
		const response = await submitPrimary({ accountId: account.id });
		if (response.success) {
			onSuccess();
		}
	};

	const onSync = async () => {
		const response = await syncToHubSpot({ accountId: account.id });
		if (response.success) {
			onSuccess();
		}
	};

	const onCloseModal = () => {
		setShowModal(false);
	};

	return (
		<StandardModal
			isOpen={showModal}
			onClose={onCloseModal}
			header={'Edit HubSpot connection'}
			footer={
				<Button
					text="Close"
					size="sm"
					variant="secondary"
					onClick={onCloseModal}
				/>
			}
		>
			<VStack gap="4xl">
				<VStack gap="xs">
					{account.hubSpotId && account.hubSpotId > 0 && (
						<>
							<Text>
								{account.isPrimary
									? `${account.firstName} ${account.lastName} is the primary contact for HubSpot ID: ${account.hubSpotId}.`
									: `${account.firstName} ${account.lastName} is not the primary contact for HubSpot ID: ${account.hubSpotId}. 
										Marking this user as primary contact will make any user sharing this HubSpot ID a non-primary contact.`}
							</Text>
							<Checkbox
								label="Primary contact"
								checked={isPrimary}
								onChange={(event) =>
									setIsPrimary(event.target.checked)
								}
								size="md"
							/>
							<Button
								text="Save change"
								size="sm"
								isDisabled={isPrimary === account.isPrimary}
								isLoading={
									markPrimaryStatus ===
										RequestStatus.LOADING ||
									markNonPrimaryStatus ===
										RequestStatus.LOADING
								}
								loadingText="Updating..."
								onClick={onUpdatePrimary}
							/>
						</>
					)}
				</VStack>
				<VStack gap="xs">
					<Text>Sync this account to HubSpot.</Text>
					<HStack>
						<ConditionallyDisabled
							disableIf={Boolean(
								!account.isPrimary &&
									account.hubSpotId &&
									account.hubSpotId > 0,
							)}
							tooltip="You cannot sync a non-primary contact to HubSpot."
						>
							<Button
								text="Sync to HubSpot"
								size="sm"
								variant="secondary"
								isLoading={syncStatus === RequestStatus.LOADING}
								loadingText="Syncing..."
								onClick={onSync}
							/>
						</ConditionallyDisabled>
					</HStack>
				</VStack>
			</VStack>
		</StandardModal>
	);
};
